import { Injectable, OnDestroy } from '@angular/core';
import { DataObject } from '@shared/models/data-object.model';
import { Column, Section } from '@miq/catalog';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface BroadcastChange {
  change: Column;
  firstChange?: boolean;
  action?: string | number;
}

// TODO: Add Angular decorator.
@Injectable()
export abstract class Broadcaster implements OnDestroy {
  private _changeEvent = new BehaviorSubject<BroadcastChange>(null);
  private _initStream = new BehaviorSubject<{ [key: number]: Column }>({});

  private actionContext = {};

  changeEventBus$: Observable<BroadcastChange> = this._changeEvent.asObservable().pipe(filter(x => Boolean(x)));
  initStream$: Observable<{ [key: number]: Column }> = this._initStream.asObservable();

  ngOnDestroy(): void {
    this._changeEvent.complete();
    this._initStream.complete();
  }

  public newEventHook<T>(
    subscriptionAction: string | number,
    record: DataObject,
    section: Section,
    eventHandler: (record: DataObject, column: Column, section: Section, actionContext: DataObject) => T
  ): Observable<T> {
    this.actionContext[subscriptionAction] = {};
    return this.subscribeToAction(subscriptionAction).pipe(
      map((change: BroadcastChange) => {
        return eventHandler(record, change.change, section, this.actionContext[subscriptionAction]) as T;
      })
    );
  }

  public subscribeToAction(subscriptionKey: string | number): Observable<BroadcastChange> {
    return this.changeEventBus$.pipe(filter((change: BroadcastChange) => change.action === subscriptionKey));
  }

  /**
   * broadcast changes, firstChange goes to alternate store to keep all publishers
   */
  public broadcast(change: BroadcastChange) {
    if (change.firstChange) {
      const state = this._initStream.value;
      this._initStream.next({ ...state, [change.change.columnId]: change.change });
    } else {
      this._changeEvent.next(change);
    }
  }

  /**
   * returns observable filtered events by targeted column id on change streams
   */
  getTargetedChangesStream(targetColumnId: number) {
    return this.changeEventBus$.pipe(filter(({ change }) => change.columnId === targetColumnId));
  }

  /**
   * returns observable filtered events by targeted column id on initialization streams
   */
  getTargetedInitializerStream(targetColumnId: number) {
    return this.initStream$.pipe(
      map(changeStore => changeStore[targetColumnId]),
      filter(change => Boolean(change))
    );
  }
}
